import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Users, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Calendar,
  TrendingUp, 
  RefreshCw,
  Eye,
  Pause,
  Play,
  MoreVertical,
  Search,
  Filter,
  Settings,
  Key,
  Globe,
  Shield,
  Save,
  Edit,
  Trash2,
  Plus,
  X,
  MessageCircle
} from 'lucide-react';
import './WhatsAppAdmin.css';

// API function (same as in App.js)
const api = (path, opts={}) => {
  const base = (process.env.REACT_APP_BACKEND_URL || '/api');
  const headers = { ...(opts.headers||{}) };
  return fetch(base + path, { 
    credentials: 'include', 
    ...opts, 
    headers: { 'Content-Type': 'application/json', ...headers }
  });
};

const WhatsAppAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [assistantStatus, setAssistantStatus] = useState(null);
  const [assistantSessions, setAssistantSessions] = useState([]);
  const [assistantPagination, setAssistantPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState('');
  const [assistantFilters, setAssistantFilters] = useState({ linked: 'all', status: '' });
  const [selectedAssistantSession, setSelectedAssistantSession] = useState(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // API Configuration state
  const [apiConfig, setApiConfig] = useState({
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    webhookVerifyToken: '',
    webhookUrl: '',
    displayName: 'Revstar Business'
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState('');
  const [configSuccess, setConfigSuccess] = useState('');

  // Fetch connections
  const fetchConnections = async (page = 1) => {
    try {
      setLoading(true);
      console.log('Fetching WhatsApp connections...');
      const response = await api(`/whatsapp/web/admin/connections?page=${page}&limit=20&status=${filterStatus}&search=${searchTerm}`);
      console.log('Connections response status:', response.status);
      const data = await response.json();
      console.log('Connections response data:', data);

      if (data.success) {
        setConnections(data.connections || []);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        setError(''); // Clear any previous errors
        console.log('Connections fetched successfully:', data.connections?.length || 0, 'connections');
      } else {
        console.error('Failed to fetch connections:', data.error);
        setConnections([]);
        setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
        setError(data.error || 'Failed to fetch connections');
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      setConnections([]);
      setError('Failed to fetch connections');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      console.log('Fetching WhatsApp health stats...');
      const response = await api('/whatsapp/web/admin/health');
      console.log('Health response status:', response.status);
      const data = await response.json();
      console.log('Health response data:', data);

      if (data.success) {
        setHealth(data.health);
        console.log('Health stats fetched successfully:', data.health);
      } else {
        console.error('Failed to fetch health stats:', data.error);
        setHealth(null);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setHealth(null);
    }
  };

  // Fetch API configuration
  const fetchApiConfig = async () => {
    try {
      const response = await api('/whatsapp/admin/config');
      const data = await response.json();
      
      if (data.success) {
        setApiConfig(data.config || {});
      } else {
        console.error('Failed to fetch API config:', data.error);
        setApiConfig({});
      }
    } catch (error) {
      console.error('Error fetching API config:', error);
      setApiConfig({});
    }
  };

  const fetchAssistantStatus = async () => {
    try {
      const response = await api('/admin/whatsapp/assistant/status');
      const data = await response.json();
      if (data.success) {
        setAssistantStatus(data.data || null);
        setAssistantError('');
      } else {
        setAssistantStatus(null);
        setAssistantError(data.error || 'Failed to fetch assistant status');
      }
    } catch (error) {
      setAssistantStatus(null);
      setAssistantError('Failed to fetch assistant status');
    }
  };

  const fetchAssistantSessions = async (page = 1) => {
    try {
      setAssistantLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(assistantPagination.limit || 20)
      });
      if (assistantFilters.linked !== 'all') params.set('linked', assistantFilters.linked);
      if (assistantFilters.status) params.set('status', assistantFilters.status);

      const response = await api(`/admin/whatsapp/assistant/sessions?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setAssistantSessions(data.data || []);
        setAssistantPagination(data.pagination || { page, limit: 20, total: 0, totalPages: 0 });
        setAssistantError('');
      } else {
        setAssistantSessions([]);
        setAssistantError(data.error || 'Failed to fetch assistant sessions');
      }
    } catch (error) {
      setAssistantSessions([]);
      setAssistantError('Failed to fetch assistant sessions');
    } finally {
      setAssistantLoading(false);
    }
  };

  const fetchAssistantSessionDetail = async (sessionId) => {
    try {
      setSessionDetailLoading(true);
      const response = await api(`/admin/whatsapp/assistant/sessions/${sessionId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedAssistantSession(data.data);
      } else {
        setAssistantError(data.error || 'Failed to fetch assistant session');
      }
    } catch (error) {
      setAssistantError('Failed to fetch assistant session');
    } finally {
      setSessionDetailLoading(false);
    }
  };

  // Save API configuration
  const saveApiConfig = async () => {
    try {
      setConfigLoading(true);
      setConfigError('');
      setConfigSuccess('');

      const response = await api('/whatsapp/admin/config', {
        method: 'POST',
        body: JSON.stringify(apiConfig)
      });

      const data = await response.json();
      
      if (data.success) {
        setConfigSuccess('WhatsApp API configuration saved successfully!');
        setTimeout(() => setConfigSuccess(''), 3000);
      } else {
        setConfigError(data.error || 'Failed to save configuration');
      }
    } catch (error) {
      setConfigError('Failed to save configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  // Suspend/unsuspend connection
  const toggleConnectionStatus = async (connectionId, suspend) => {
    try {
      const response = await api(`/whatsapp/admin/connections/${connectionId}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ 
          suspend,
          reason: suspend ? 'Admin action' : 'Admin restored'
        })
      });
      
      if (response.ok) {
        await fetchConnections();
        await fetchStats();
      }
    } catch (error) {
      console.error('Error toggling connection status:', error);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' };
      case 'inactive':
        return { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
      case 'suspended':
        return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
      case 'expired':
        return { bg: '#fef3c7', text: '#d97706', border: '#fed7aa' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} color="#10b981" />;
      case 'inactive':
        return <Clock size={16} color="#6b7280" />;
      case 'suspended':
        return <Pause size={16} color="#dc2626" />;
      case 'expired':
        return <AlertTriangle size={16} color="#d97706" />;
      default:
        return <Clock size={16} color="#6b7280" />;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  useEffect(() => {
    fetchConnections();
    fetchStats();
    fetchApiConfig();
  }, [filterStatus, searchTerm]);

  useEffect(() => {
    if (activeTab === 'assistant') {
      fetchAssistantStatus();
      fetchAssistantSessions(1);
    }
  }, [activeTab, assistantFilters.linked, assistantFilters.status]);

  const statsCards = [
    {
      title: 'Total Connections',
      value: health?.totalConnections || 0,
      icon: MessageSquare
    },
    {
      title: 'Active Connections',
      value: health?.activeConnections || 0,
      icon: CheckCircle
    },
    {
      title: 'Success Rate',
      value: health?.performance?.totalWebhooks > 0 
        ? Math.round((health.performance.successfulWebhooks / health.performance.totalWebhooks) * 100)
        : 0,
      suffix: '%',
      icon: TrendingUp
    },
    {
      title: 'Error Rate',
      value: health?.errors?.totalErrors || 0,
      icon: AlertTriangle
    }
  ];

  const assistantCards = [
    {
      title: 'Total Sessions',
      value: assistantStatus?.sessions?.total || 0,
      subvalue: `${assistantStatus?.sessions?.linked || 0} linked`,
      icon: MessageCircle,
      tone: 'primary'
    },
    {
      title: 'Prospects',
      value: assistantStatus?.sessions?.prospects || 0,
      subvalue: 'Not linked yet',
      icon: Users,
      tone: 'blue'
    },
    {
      title: 'Messages Today',
      value: (assistantStatus?.messages?.todayInbound || 0) + (assistantStatus?.messages?.todayOutbound || 0),
      subvalue: `${assistantStatus?.messages?.todayInbound || 0} in / ${assistantStatus?.messages?.todayOutbound || 0} out`,
      icon: Activity,
      tone: 'green'
    },
    {
      title: 'Needs Attention',
      value: (assistantStatus?.messages?.failedOutbound || 0) + (assistantStatus?.handoffs?.expired || 0),
      subvalue: `${assistantStatus?.messages?.failedOutbound || 0} failed, ${assistantStatus?.handoffs?.expired || 0} expired handoffs`,
      icon: AlertTriangle,
      tone: 'amber'
    },
    {
      title: 'Pending Actions',
      value: assistantStatus?.actions?.pending || 0,
      subvalue: 'Awaiting merchant confirm',
      icon: Clock,
      tone: 'purple'
    },
    {
      title: 'Active Schedules',
      value: assistantStatus?.schedules?.active || 0,
      subvalue: 'Alerts and digests',
      icon: Calendar,
      tone: 'slate'
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MessageCircle size={32} color="var(--brand)" />
            <div>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text)' }}>
                WhatsApp Administration
              </h1>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '16px' }}>
                Manage WhatsApp Business API connections and monitor system health
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setError('');
              fetchConnections();
              fetchStats();
            }}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
        
        {/* Error Display */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => setActiveTab('overview')}
            className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Activity size={16} />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('connections')}
            className={`btn ${activeTab === 'connections' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Users size={16} />
            Connections
          </button>
          <button
            onClick={() => setActiveTab('assistant')}
            className={`btn ${activeTab === 'assistant' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <MessageCircle size={16} />
            Assistant
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`btn ${activeTab === 'config' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Settings size={16} />
            API Configuration
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Stats Cards */}
          <div className="cards-4">
            {statsCards.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="icon">
                  <stat.icon size={24} color="var(--brand)" />
                </div>
                <div className="label">{stat.title}</div>
                <div className="value">
                  {stat.value}{stat.suffix || ''}
                  {stat.total && (
                    <span style={{ fontSize: '14px', fontWeight: '400', color: 'var(--text-muted)' }}>
                      / {stat.total}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* System Health */}
          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={18} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>System Health</h3>
              </div>
            </div>
            <div className="card-body">
              {health ? (
                <div className="cards-4">
                  <div className="stat-card">
                    <div className="icon">
                      <Activity size={24} color="var(--brand)" />
                    </div>
                    <div className="label">Total Webhooks</div>
                    <div className="value">{health.performance?.totalWebhooks || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="icon">
                      <CheckCircle size={24} color="var(--brand)" />
                    </div>
                    <div className="label">Successful Webhooks</div>
                    <div className="value">{health.performance?.successfulWebhooks || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="icon">
                      <AlertTriangle size={24} color="var(--brand)" />
                    </div>
                    <div className="label">Failed Webhooks</div>
                    <div className="value">{health.performance?.failedWebhooks || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="icon">
                      <Clock size={24} color="var(--brand)" />
                    </div>
                    <div className="label">Avg Processing Time</div>
                    <div className="value">{health.performance?.avgProcessingTime || 0}ms</div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  {loading ? 'Loading system health data...' : 'No health data available'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connections Tab */}
      {activeTab === 'connections' && (
        <div>
          {/* Search and Filter */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-body">
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    placeholder="Search by phone number or display name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input"
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="input"
                  style={{ width: 'auto' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* Connections Table */}
          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={18} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>WhatsApp Connections</h3>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  Loading connections...
                </div>
              ) : connections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-muted)' }}>No WhatsApp Connections</h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>No merchants have connected their WhatsApp Business accounts yet.</p>
                </div>
              ) : (
                <div style={{ overflow: 'auto', margin: '0 -16px' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Phone Number</th>
                        <th>Status</th>
                        <th>Last Activity</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.map((connection) => {
                        const statusColors = getStatusColor(connection.status);
                        return (
                          <tr key={connection._id}>
                            <td>
                              <div>
                                <p style={{ margin: '0 0 4px 0', fontWeight: '500', color: 'var(--text)' }}>
                                  {connection.merchantId?.firstName} {connection.merchantId?.lastName}
                                </p>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                                  {connection.merchantId?.email}
                                </p>
                              </div>
                            </td>
                            <td>
                              <p style={{ margin: 0, fontWeight: '500', color: 'var(--text)' }}>
                                {connection.phoneNumber}
                              </p>
                            </td>
                            <td>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                backgroundColor: statusColors.bg,
                                color: statusColors.text,
                                border: `1px solid ${statusColors.border}`,
                                fontSize: '14px',
                                fontWeight: '500'
                              }}>
                                {getStatusIcon(connection.status)}
                                {connection.status}
                              </div>
                            </td>
                            <td>
                              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                                {formatTimestamp(connection.lastMessageSent)}
                              </p>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => setSelectedConnection(connection)}
                                  className="btn btn-secondary"
                                  style={{ padding: '6px 12px', fontSize: '14px' }}
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => toggleConnectionStatus(connection._id, connection.status !== 'suspended')}
                                  className={`btn ${connection.status === 'suspended' ? 'btn-primary' : 'btn-danger'}`}
                                  style={{ padding: '6px 12px', fontSize: '14px' }}
                                >
                                  {connection.status === 'suspended' ? <Play size={14} /> : <Pause size={14} />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assistant Tab */}
      {activeTab === 'assistant' && (
        <div className="waa-shell">
          {assistantError && (
            <div className="waa-alert waa-alert-danger">
              <AlertTriangle size={16} />
              <span>{assistantError}</span>
            </div>
          )}

          <div className="waa-toolbar">
            <div>
              <h2>Revstar-owned Assistant</h2>
              <p>Track Layer 1 merchant and prospect conversations, failed sends, pending actions, schedules, and handoffs.</p>
            </div>
            <button
              type="button"
              className="waa-icon-button waa-icon-button-primary"
              onClick={() => {
                fetchAssistantStatus();
                fetchAssistantSessions(assistantPagination.page || 1);
              }}
              title="Refresh assistant monitoring"
              aria-label="Refresh assistant monitoring"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          <div className="waa-metrics">
            {assistantCards.map((card) => (
              <div className={`waa-metric waa-metric-${card.tone}`} key={card.title}>
                <div className="waa-metric-icon">
                  <card.icon size={18} />
                </div>
                <div className="waa-metric-copy">
                  <span>{card.title}</span>
                  <strong>{card.value}</strong>
                  <small>{card.subvalue}</small>
                </div>
              </div>
            ))}
          </div>

          <div className="waa-panel">
            <div className="waa-panel-head">
              <div>
                <h3>Assistant Sessions</h3>
                <p>Review linked merchants, prospects, message counts, pending actions, schedules, and recent handoffs.</p>
              </div>
              <div className="waa-filters">
                <label>
                  <span>Linked</span>
                  <select
                    value={assistantFilters.linked}
                    onChange={(event) => setAssistantFilters((current) => ({ ...current, linked: event.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="true">Linked</option>
                    <option value="false">Unlinked</option>
                  </select>
                </label>
                <label>
                  <span>Status</span>
                  <select
                    value={assistantFilters.status}
                    onChange={(event) => setAssistantFilters((current) => ({ ...current, status: event.target.value }))}
                  >
                    <option value="">All statuses</option>
                    <option value="NEW">New</option>
                    <option value="LINKING">Linking</option>
                    <option value="LINKED">Linked</option>
                    <option value="PROSPECT">Prospect</option>
                    <option value="DISABLED">Disabled</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="waa-table-wrap">
              <table className="waa-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Merchant</th>
                    <th>Status</th>
                    <th>Activity</th>
                    <th>Workload</th>
                    <th>Last Intent</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {assistantLoading ? (
                    <tr>
                      <td colSpan="7">
                        <div className="waa-empty">Loading assistant sessions...</div>
                      </td>
                    </tr>
                  ) : assistantSessions.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="waa-empty">No assistant sessions match this filter.</div>
                      </td>
                    </tr>
                  ) : (
                    assistantSessions.map((session) => (
                      <tr key={session.id}>
                        <td>
                          <div className="waa-person">
                            <strong>{session.displayName || session.waPhone || 'Unknown contact'}</strong>
                            <span>{session.waPhone || 'No phone'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="waa-person">
                            <strong>{session.merchant?.store?.name || 'Not linked'}</strong>
                            <span>{session.merchant?.email || 'Prospect or anonymous'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="waa-status-stack">
                            <span className={`waa-pill ${session.isLinked ? 'waa-pill-green' : 'waa-pill-muted'}`}>
                              {session.isLinked ? 'Linked' : 'Unlinked'}
                            </span>
                            <span className="waa-stage">{session.status || 'UNKNOWN'} / {session.stage || 'NONE'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="waa-person">
                            <strong>{formatTimestamp(session.lastMessageAt)}</strong>
                            <span>Created {formatTimestamp(session.createdAt)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="waa-counts">
                            <span>{session._count?.messages || 0} msgs</span>
                            <span>{session._count?.pendingActions || 0} actions</span>
                            <span>{session._count?.schedules || 0} schedules</span>
                          </div>
                        </td>
                        <td>
                          <span className="waa-intent">{session.lastIntent || 'None'}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="waa-icon-button"
                            onClick={() => fetchAssistantSessionDetail(session.id)}
                            title="View session detail"
                            aria-label="View assistant session detail"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="waa-pagination">
              <span>
                Page {assistantPagination.page || 1} of {assistantPagination.totalPages || 1}
                {assistantPagination.total ? ` · ${assistantPagination.total} sessions` : ''}
              </span>
              <div>
                <button
                  type="button"
                  className="waa-page-button"
                  disabled={(assistantPagination.page || 1) <= 1 || assistantLoading}
                  onClick={() => fetchAssistantSessions((assistantPagination.page || 1) - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="waa-page-button"
                  disabled={(assistantPagination.page || 1) >= (assistantPagination.totalPages || 1) || assistantLoading}
                  onClick={() => fetchAssistantSessions((assistantPagination.page || 1) + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Configuration Tab */}
      {activeTab === 'config' && (
        <div>
          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={18} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>WhatsApp Business API Configuration</h3>
              </div>
            </div>
            <div className="card-body">
              {configError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertTriangle size={16} />
                  {configError}
                </div>
              )}

              {configSuccess && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#dcfce7',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  color: '#166534',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <CheckCircle size={16} />
                  {configSuccess}
                </div>
              )}

              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                    Access Token *
                  </label>
                  <input
                    type="password"
                    value={apiConfig.accessToken}
                    onChange={(e) => setApiConfig({...apiConfig, accessToken: e.target.value})}
                    placeholder="Enter WhatsApp Business API access token"
                    className="input"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                      Phone Number ID *
                    </label>
                    <input
                      type="text"
                      value={apiConfig.phoneNumberId}
                      onChange={(e) => setApiConfig({...apiConfig, phoneNumberId: e.target.value})}
                      placeholder="Enter phone number ID"
                      className="input"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                      Business Account ID *
                    </label>
                    <input
                      type="text"
                      value={apiConfig.businessAccountId}
                      onChange={(e) => setApiConfig({...apiConfig, businessAccountId: e.target.value})}
                      placeholder="Enter business account ID"
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                    Webhook Verify Token *
                  </label>
                  <input
                    type="password"
                    value={apiConfig.webhookVerifyToken}
                    onChange={(e) => setApiConfig({...apiConfig, webhookVerifyToken: e.target.value})}
                    placeholder="Enter webhook verification token"
                    className="input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                    Webhook URL *
                  </label>
                  <input
                    type="url"
                    value={apiConfig.webhookUrl}
                    onChange={(e) => setApiConfig({...apiConfig, webhookUrl: e.target.value})}
                    placeholder="https://yourdomain.com/api/whatsapp/webhook"
                    className="input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text)' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={apiConfig.displayName}
                    onChange={(e) => setApiConfig({...apiConfig, displayName: e.target.value})}
                    placeholder="Enter business display name"
                    className="input"
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={saveApiConfig}
                    disabled={configLoading}
                    className="btn btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {configLoading ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                    {configLoading ? 'Saving...' : 'Save Configuration'}
                  </button>
                  <button
                    onClick={fetchApiConfig}
                    className="btn btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assistant Session Detail Modal */}
      {selectedAssistantSession && (
        <div className="waa-modal-backdrop" onClick={() => setSelectedAssistantSession(null)}>
          <div className="waa-modal" onClick={(event) => event.stopPropagation()}>
            <div className="waa-modal-head">
              <div>
                <h3>{selectedAssistantSession.displayName || selectedAssistantSession.waPhone || 'Assistant session'}</h3>
                <p>{selectedAssistantSession.merchant?.email || selectedAssistantSession.email || 'No linked merchant email'}</p>
              </div>
              <button
                type="button"
                className="waa-icon-button"
                onClick={() => setSelectedAssistantSession(null)}
                title="Close assistant session detail"
                aria-label="Close assistant session detail"
              >
                <X size={16} />
              </button>
            </div>

            {sessionDetailLoading ? (
              <div className="waa-empty">Loading session detail...</div>
            ) : (
              <div className="waa-detail-grid">
                <section className="waa-detail-section">
                  <h4>Session</h4>
                  <dl className="waa-definition-list">
                    <div>
                      <dt>Phone</dt>
                      <dd>{selectedAssistantSession.waPhone || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{selectedAssistantSession.status || 'N/A'} / {selectedAssistantSession.stage || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt>Linked</dt>
                      <dd>{selectedAssistantSession.isLinked ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>Last message</dt>
                      <dd>{formatTimestamp(selectedAssistantSession.lastMessageAt)}</dd>
                    </div>
                    <div>
                      <dt>Last intent</dt>
                      <dd>{selectedAssistantSession.lastIntent || 'None'}</dd>
                    </div>
                  </dl>
                </section>

                <section className="waa-detail-section">
                  <h4>Schedules</h4>
                  <div className="waa-list">
                    {(selectedAssistantSession.schedules || []).length === 0 ? (
                      <span className="waa-muted">No schedules</span>
                    ) : selectedAssistantSession.schedules.map((schedule) => (
                      <div className="waa-list-row" key={schedule.id}>
                        <div>
                          <strong>{schedule.scheduleType}</strong>
                          <span>{schedule.timezone || 'No timezone'} · next {formatTimestamp(schedule.nextRunAt)}</span>
                        </div>
                        <span className={`waa-pill ${schedule.status === 'ACTIVE' ? 'waa-pill-green' : 'waa-pill-muted'}`}>
                          {schedule.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="waa-detail-section">
                  <h4>Pending Actions</h4>
                  <div className="waa-list">
                    {(selectedAssistantSession.pendingActions || []).length === 0 ? (
                      <span className="waa-muted">No pending actions</span>
                    ) : selectedAssistantSession.pendingActions.map((action) => (
                      <div className="waa-list-row" key={action.id}>
                        <div>
                          <strong>{action.actionType}</strong>
                          <span>{action.riskLevel || 'LOW'} risk · expires {formatTimestamp(action.expiresAt)}</span>
                        </div>
                        <span className={`waa-pill ${action.status === 'PENDING' ? 'waa-pill-amber' : 'waa-pill-muted'}`}>
                          {action.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="waa-detail-section">
                  <h4>Handoffs</h4>
                  <div className="waa-list">
                    {(selectedAssistantSession.handoffs || []).length === 0 ? (
                      <span className="waa-muted">No handoffs</span>
                    ) : selectedAssistantSession.handoffs.map((handoff) => (
                      <div className="waa-list-row" key={handoff.id}>
                        <div>
                          <strong>{handoff.purpose}</strong>
                          <span>{handoff.targetPath || 'No target'} · expires {formatTimestamp(handoff.expiresAt)}</span>
                        </div>
                        <span className={`waa-pill ${handoff.status === 'OPENED' ? 'waa-pill-green' : 'waa-pill-muted'}`}>
                          {handoff.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="waa-detail-section waa-detail-wide">
                  <h4>Recent Messages</h4>
                  <div className="waa-message-list">
                    {(selectedAssistantSession.messages || []).length === 0 ? (
                      <span className="waa-muted">No messages</span>
                    ) : selectedAssistantSession.messages.map((message) => (
                      <div className={`waa-message waa-message-${String(message.direction || '').toLowerCase()}`} key={message.id}>
                        <div className="waa-message-meta">
                          <strong>{message.direction}</strong>
                          <span>{message.status} · {formatTimestamp(message.createdAt)}</span>
                        </div>
                        <p>{message.text || message.errorMessage || 'Non-text message'}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection Details Modal */}
      {selectedConnection && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Connection Details</h3>
              <button
                onClick={() => setSelectedConnection(null)}
                className="btn btn-secondary"
                style={{ padding: '8px' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Merchant</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>
                    {selectedConnection.merchantId?.firstName} {selectedConnection.merchantId?.lastName}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                    {selectedConnection.merchantId?.email}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Phone Number</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>{selectedConnection.phoneNumber}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Display Name</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>{selectedConnection.displayName}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Status</p>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: getStatusColor(selectedConnection.status).bg,
                    color: getStatusColor(selectedConnection.status).text,
                    border: `1px solid ${getStatusColor(selectedConnection.status).border}`,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {getStatusIcon(selectedConnection.status)}
                    {selectedConnection.status}
                  </div>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Created</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>{formatTimestamp(selectedConnection.createdAt)}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Last Message</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>{formatTimestamp(selectedConnection.lastMessageSent)}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>Last Webhook</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>{formatTimestamp(selectedConnection.lastWebhookReceived)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppAdmin;
